/**
 * API client.
 *
 * Deliberately ~80 lines rather than a data-fetching library. The product is
 * about twenty form-driven CRUD screens; a cache layer would be more moving
 * parts than the screens themselves. Architecture §15 asks for exactly this.
 * If real cache-invalidation pain shows up later, this is the one file to
 * replace.
 */

import type { ErrorCode } from './error-messages.js';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    code: ErrorCode,
    status: number,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** File uploads. Mutually exclusive with `body`. */
  formData?: FormData;
  /** For POST /api/reports/generate — API §23. */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, formData, idempotencyKey, signal } = options;

  const headers: Record<string, string> = {};
  // Content-Type is deliberately not set for FormData: the browser has to add
  // it itself so it can include the multipart boundary.
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      // The session is an HttpOnly cookie. No token ever reaches JavaScript
      // (Architecture §6.3), so every request must carry credentials.
      credentials: 'include',
      body: formData ?? (body === undefined ? undefined : JSON.stringify(body)),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError('NETWORK_ERROR', 0, 'Network request failed.');
  }

  if (response.status === 204) return undefined as T;

  // A 200 whose body is not JSON is a broken response, not an empty one.
  // Silently treating it as `null` makes an HTML error page — a dev-server
  // fallback, a proxy timeout, a captive portal — look like a successful
  // API call, and the failure then surfaces somewhere unrelated.
  const contentType = response.headers.get('Content-Type') ?? '';
  const looksJson = contentType.includes('json');

  if (response.ok && !looksJson) {
    throw new ApiError('INTERNAL_ERROR', response.status, 'Expected a JSON response.');
  }

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    if (response.ok) {
      throw new ApiError('INTERNAL_ERROR', response.status, 'Malformed JSON response.');
    }
  }

  if (!response.ok) {
    const err = readErrorEnvelope(payload);
    throw new ApiError(
      err.code ?? 'INTERNAL_ERROR',
      response.status,
      err.message ?? 'Request failed.',
      err.details,
    );
  }

  return payload as T;
}

interface ParsedError {
  code?: ErrorCode;
  message?: string;
  details?: Record<string, unknown>;
}

/**
 * Reads the API error envelope defensively.
 *
 * A failing response is exactly the case where the body may not be the shape we
 * expect — a proxy error page, an empty body, a truncated response. Parsing it
 * optimistically would turn a server error into a confusing client crash.
 */
function readErrorEnvelope(payload: unknown): ParsedError {
  if (typeof payload !== 'object' || payload === null) return {};

  const envelope = (payload as Record<string, unknown>).error;
  if (typeof envelope !== 'object' || envelope === null) return {};

  const fields = envelope as Record<string, unknown>;
  const result: ParsedError = {};

  if (typeof fields.code === 'string') result.code = fields.code as ErrorCode;
  if (typeof fields.message === 'string') result.message = fields.message;
  if (typeof fields.details === 'object' && fields.details !== null) {
    result.details = fields.details as Record<string, unknown>;
  }

  return result;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown, idempotencyKey?: string) =>
    request<T>(path, {
      method: 'POST',
      ...(body !== undefined ? { body } : {}),
      ...(idempotencyKey ? { idempotencyKey } : {}),
    }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
